import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";

const SELECT = "SELECT";
const FORM_NAME = "SBIMPF";
const PDF_ENDPOINT = "/FillMyForm/SBIMPF/Print.php";

const customerFields = [
  { name: "branch", label: "Branch Name", maxLength: 25, required: true },
  { name: "acno", label: "Account Number", maxLength: 11, numeric: true, required: true },
  { name: "name", label: "Applicant Name", maxLength: 40, alphaOnly: true, required: true },
];

const requests = [
  { id: "chkcb", title: "Issue Cheque Book", fields: [
    { name: "cbtype", label: "Cheque Book Type", type: "select", required: true,
      options: [["20P","20 Pages"],["50P","50 Pages"],["100P","100 Pages"]] },
  ]},
  { id: "chkta", title: "Account Transfer", fields: [
    { name: "bname", label: "New Branch Name", maxLength: 20, required: true },
    { name: "bcode", label: "New Branch Code", maxLength: 5, numeric: true, required: true },
  ]},
  { id: "chkca", title: "Closure of Account", fields: [] },
  { id: "chkdp", title: "Duplicate Passbook", fields: [] },
  { id: "chkcm", title: "Change Mobile Number", fields: [
    { name: "cmobile", label: "New Mobile Number", maxLength: 10, numeric: true, required: true },
  ]},
  { id: "chksms", title: "SMS Alert", fields: [
    { name: "smobile", label: "SMS Alert Mobile Number", maxLength: 10, numeric: true, required: true },
  ]},
  { id: "chkemail", title: "Change Email ID", fields: [
    { name: "email", label: "New Email ID", type: "email", maxLength: 50, required: true },
  ]},
];

const allRequestFields = requests.flatMap((request) => request.fields);
const initialFormData = Object.fromEntries(
  [...customerFields, ...allRequestFields].map((field) => [
    field.name,
    field.type === "select" ? SELECT : "",
  ]),
);
const initialSelected = Object.fromEntries(requests.map((request) => [request.id, false]));

async function readJson(response) {
  const text = await response.text();
  if (!text.trim()) return null;
  try { return JSON.parse(text); }
  catch { throw new Error("The server returned an invalid JSON response."); }
}

function optionLabel(field, value) {
  if (!field.options || !value || value === SELECT) return "-";
  return field.options.find(([v]) => v === value)?.[1] || value;
}

export default function SBIMPF() {
  const [formData, setFormData] = useState(initialFormData);
  const [selected, setSelected] = useState(initialSelected);
  const [errors, setErrors] = useState({});
  const [selectionError, setSelectionError] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);

  const selectedRequests = requests.filter((request) => selected[request.id]);

  useEffect(() => {
    if (currentTab !== 0) return undefined;
    const frame = requestAnimationFrame(() => document.getElementById("branch")?.focus());
    return () => cancelAnimationFrame(frame);
  }, [currentTab]);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent(FORM_NAME)}`,
          { credentials: "include", cache: "no-store", headers: { Accept: "application/json" }, signal: controller.signal },
        );
        const result = await readJson(response);
        if (!response.ok || !result?.success || !result?.found || !result?.data) return;
        setFormData((p) => ({ ...p, branch: p.branch || result.data.branch || "" }));
      } catch (error) {
        if (error.name !== "AbortError") console.error("SBIMPF autofill error:", error);
      }
    })();
    return () => controller.abort();
  }, []);

  useEffect(() => () => {
    if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const updateField = (field, rawValue) => {
    let value = rawValue;
    if (field.numeric) value = value.replace(/\\D/g, "");
    if (field.alphaOnly) value = value.replace(/[^A-Za-z ]/g, "");
    if (field.uppercase || field.name.includes("pan")) value = value.toUpperCase();
    setFormData((p) => ({ ...p, [field.name]: value }));
    setErrors((p) => ({ ...p, [field.name]: "" }));
  };

  const toggleRequest = (request) => {
    const checked = !selected[request.id];
    setSelected((p) => ({ ...p, [request.id]: checked }));
    setSelectionError("");
    if (!checked) {
      const cleared = {};
      request.fields.forEach((field) => {
        cleared[field.name] = field.type === "select" ? SELECT : "";
      });
      setFormData((p) => ({ ...p, ...cleared }));
    }
  };

  const validateField = (field) => {
    const value = String(formData[field.name] ?? "").trim();
    if (field.required && (field.type === "select" ? !value || value === SELECT : !value)) {
      return `${field.label} is required.`;
    }
    if (field.alphaOnly && value && !/^[A-Za-z ]+$/.test(value)) {
      return `${field.label} must contain alphabet letters only.`;
    }
    if (field.name === "acno" && value && !/^\d{11}$/.test(value)) return "Account number must contain 11 digits.";
    if (["cmobile","smobile"].includes(field.name) && value && !/^\d{10}$/.test(value)) return "Mobile number must contain 10 digits.";
    if (field.name === "aadhaar" && value && !/^\d{12}$/.test(value)) return "Aadhaar number must contain exactly 12 digits.";
    if (field.name === "pin" && value && !/^\d{6}$/.test(value)) return "PIN must contain exactly 6 digits.";
    if (field.name === "pan" && value && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value)) return "Enter a valid PAN number (e.g. ABCDE1234F).";
    if (field.name === "email" && value && !/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(value)) return "Enter a valid email address.";
    return "";
  };

  const validateAll = () => {
    const next = {};
    customerFields.forEach((field) => {
      const message = validateField(field);
      if (message) next[field.name] = message;
    });
    if (selectedRequests.length === 0) {
      setSelectionError("Please choose at least one request option.");
    } else {
      setSelectionError("");
      selectedRequests.flatMap((request) => request.fields).forEach((field) => {
        const message = validateField(field);
        if (message) next[field.name] = message;
      });
    }
    setErrors(next);
    return Object.keys(next).length === 0 && selectedRequests.length > 0;
  };

  const goNext = () => {
    if (!validateAll()) return;
    setCurrentTab(1);
    setMaxAllowedTab(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrevious = () => {
    setCurrentTab(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLastFieldKeyDown = (event, fieldName) => {
    const activeFields = [...customerFields, ...selectedRequests.flatMap((request) => request.fields)];
    const lastName = activeFields[activeFields.length - 1]?.name;
    if (fieldName === lastName && event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateAll()) {
      setCurrentTab(0);
      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();
      customerFields.forEach((field) => body.append(field.name, String(formData[field.name] ?? "")));

      selectedRequests.forEach((request) => {
        body.append(request.id, request.id);
        request.fields.forEach((field) => {
          const value = formData[field.name];
          body.append(field.name, value === SELECT ? "" : String(value ?? ""));
        });
      });

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 60000);
      let response;
      try {
        response = await fetch(PDF_ENDPOINT, {
          method: "POST",
          body,
          credentials: "include",
          headers: { Accept: "application/pdf" },
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeout);
      }

      if (!response.ok) throw new Error((await response.text()) || `PDF generation failed (${response.status}).`);
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/pdf")) throw new Error((await response.text()) || "Print.php did not return a PDF file.");

      const url = URL.createObjectURL(await response.blob());
      setPdfUrl((old) => {
        if (old?.startsWith("blob:")) URL.revokeObjectURL(old);
        return url;
      });
      setShowPdf(true);
    } catch (error) {
      setPdfError(error.name === "AbortError" ? "PDF generation timed out." : error.message || "Unable to generate PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.name];
    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}{field.required && <span className="text-red-500"> *</span>}
        </Label>

        {field.type === "select" ? (
          <select
            id={field.name}
            name={field.name}
            value={formData[field.name]}
            onChange={(event) => updateField(field, event.target.value)}
            onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}
            className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ${error ? "border-red-500" : ""}`}
          >
            <option value={SELECT}>SELECT</option>
            {field.options.map(([value,label]) => <option key={`${field.name}-${value}`} value={value}>{label}</option>)}
          </select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name]}
            maxLength={field.maxLength}
            placeholder={field.label}
            className={error ? "border-red-500" : ""}
            onChange={(event) => updateField(field, event.target.value)}
            onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}
          />
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return <PdfViewerContainer pdfUrl={pdfUrl} title="SBI Multi Purpose Form" onBack={() => setShowPdf(false)} />;
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">🏦 SBI Multi Purpose Form</CardTitle>
            <p className="text-muted-foreground">Enter customer details and select one or more service requests.</p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-2 gap-3">
          {["Customer & Requests","Preview"].map((tab,index) => (
            <Button key={tab} type="button" variant={currentTab === index ? "default" : "outline"} disabled={index > maxAllowedTab} onClick={() => setCurrentTab(index)}>
              {index + 1}. {tab}
            </Button>
          ))}
        </div>

        {currentTab === 0 ? (
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle>👤 Customer Details</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {customerFields.map(renderField)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>✅ Request Options</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {requests.map((request) => (
                  <div key={request.id} className="rounded-lg border p-4">
                    <label className="flex cursor-pointer items-center gap-3">
                      <input type="checkbox" checked={selected[request.id]} onChange={() => toggleRequest(request)} className="h-5 w-5" />
                      <span className="font-medium">{request.title}</span>
                    </label>

                    {selected[request.id] && request.fields.length > 0 && (
                      <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {request.fields.map(renderField)}
                      </div>
                    )}
                  </div>
                ))}
                {selectionError && <p className="text-sm text-red-500">{selectionError}</p>}
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card>
            <CardHeader><CardTitle>📋 Preview</CardTitle></CardHeader>
            <CardContent className="space-y-8">
              <section>
                <h3 className="mb-3 text-lg font-semibold">Customer Details</h3>
                <div className="overflow-hidden rounded-lg border">
                  {customerFields.map((field) => (
                    <div key={field.name} className="grid grid-cols-2 border-b p-3 last:border-b-0">
                      <span className="font-medium">{field.label}</span>
                      <span>{formData[field.name] || "-"}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-lg font-semibold">Selected Requests</h3>
                <div className="space-y-4">
                  {selectedRequests.map((request) => (
                    <div key={request.id} className="rounded-lg border p-4">
                      <h4 className="mb-3 font-semibold">{request.title}</h4>
                      {request.fields.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Selected</p>
                      ) : request.fields.map((field) => (
                        <div key={field.name} className="grid grid-cols-2 border-b py-2 last:border-b-0">
                          <span>{field.label}</span>
                          <span>{field.type === "select" ? optionLabel(field, formData[field.name]) : formData[field.name] || "-"}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            </CardContent>
          </Card>
        )}

        {pdfError && <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">{pdfError}</p>}

        <div className="mt-6 flex items-center justify-between">
          <Button type="button" variant="outline" onClick={goPrevious} disabled={currentTab === 0}>← Previous</Button>
          {currentTab === 1 ? (
            <Button type="button" ref={nextButtonRef} onClick={handleSubmit} disabled={pdfLoading}>
              {pdfLoading ? "Generating PDF..." : "Submit & Open PDF"}
            </Button>
          ) : (
            <Button type="button" ref={nextButtonRef} onClick={goNext}>Preview</Button>
          )}
        </div>
      </div>
    </div>
  );
}
